<script setup lang="ts">
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import VeeAttachments from '@/components/forms/Attachments/VeeAttachments.vue';
import { I18nKey } from '@/utils/typings';
import { useFormValues } from 'vee-validate';
import { MedicalSystemRtSystemDto } from '@/api/generator';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import VeeAvailability from '@/components/forms/Availability/VeeAvailability.vue';
import VeeImageField from '@/components/forms/ImageField/VeeImageField.vue';
import { mainNavigationMedicalSystems } from '@/components/icons/icons';
import { useFormMode } from '@/components/forms/useFormMode';
import EntityChangeBlock from '@/components/EntityHistory/EntityChangeBlock.vue';
import { createDetailImageProps } from '@/components/images/presets';

const values = useFormValues<MedicalSystemRtSystemDto>();

const mode = useFormMode();

const detailImageProps = createDetailImageProps({
  fallbackIcon: mainNavigationMedicalSystems,
});
</script>

<template>
  <v-row>
    <v-col cols="6" lg="2">
      <VeeImageField
        v-bind="detailImageProps"
        name="imageId"
        data-testid="medicalSystemEntityImage"
      />
    </v-col>
    <v-col cols="6" lg="2" :orderLg="12">
      <VeeAvailability
        name="isAvailable"
        :isReferencedBy="values.isReferencedBy ?? undefined"
        :hideReferences="true"
        :impactMessage="
          $t('DisableMedicalSystemInfoMsg', {
            entityName: $t('MedicalSystem_one'),
          })
        "
      />
      <EntityChangeBlock v-if="mode === 'details'" class="gutterTop" :entity="values" />
    </v-col>
    <v-col cols="12" lg="8">
      <v-row>
        <v-col cols="12" lg="4">
          <VeeTextField name="name" :label="$t('Name')" :placeholder="$t('Required')" />
          <VeeTextarea class="gutterTop" name="description" :label="$t('Description')" rows="7" />
          <VeeAttachments class="mt-1" name="attachments" data-testid="Description_Attachments" />
        </v-col>
        <v-col cols="6" lg="4">
          <VeeTextField name="typeSpecification" :label="$t('TypeSpecification')" />
          <VeeTextField
            v-if="'serialNumber' in values"
            class="gutterTop"
            name="serialNumber"
            :disabled="mode === 'edit' && values.isReferenced"
            :label="$t('SerialNumber')"
            :infoMessage="
              mode === 'edit' && values.isReferenced
                ? $t('MedicalDeviceSerialNumberDisabledReasonTooltipMsg')
                : undefined
            "
          />
          <VeeTextField
            v-if="'version' in values"
            class="gutterTop"
            name="version"
            :label="$t('Version')"
          />
        </v-col>
        <v-col cols="6" lg="4">
          <FormLabel v-if="values.category" :label="$t('Category_one')">{{
            $t(`RtSystemCategoryEnum.${values.category}`)
          }}</FormLabel>
          <FormLabel v-if="values.subCategory" class="gutterTop" :label="$t('Type_one')">{{
            $t(`MedicalSystemSubCategoryEnum.${values.subCategory}`)
          }}</FormLabel>
          <FormLabel v-if="values.manufacturer" class="gutterTop" :label="$t('Manufacturer')">{{
            $t(`RtSystemManufacturerEnum.${values.manufacturer}` as I18nKey)
          }}</FormLabel>
          <FormLabel v-if="values.model" class="gutterTop" :label="$t('Model')">{{
            $t(`RtSystemModelEnum.${values.model}` as I18nKey)
          }}</FormLabel>
          <FormLabel v-if="values.subModel" class="gutterTop" :label="$t('SubModel')">{{
            $t(`MedicalSystemSubModelEnum.${values.subModel}` as I18nKey)
          }}</FormLabel>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>
